import {NgTemplateOutlet} from '@angular/common';
import { Component, effect, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {ArrayUtils} from '@app/core/utils/array.utils';
import {ESortOption} from '@app/order/components/enums/sort-option.enum';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ICustomerOrganization} from '@app/shared/models/header.model';
import {ISearchResult} from '@app/shared/models/search-response.model';
import {ISearchState} from '@app/shared/models/search-state.model';
import {PrimeNgModule} from '@app/shared/modules/prime-ng/prime-ng.module';
import {SharedModule} from '@app/shared/shared.module';
import {selectCustomerOrganization} from '@app/shared/state/header/header.selector';
import {searchAction} from '@app/shared/state/search/search.actions';
import {selectSearchState} from '@app/shared/state/search/search.selectors';
import {Store} from '@ngrx/store';
import {TranslateModule} from '@ngx-translate/core';
import {PaginatorState} from 'primeng/paginator';
import {TableLazyLoadEvent} from 'primeng/table';
import {Subject, takeUntil, tap} from 'rxjs';

@Component({
  selector: 'app-search-results',
  imports: [TranslateModule, PrimeNgModule, SharedModule, NgTemplateOutlet],
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  searchState: Signal<ISearchState>;
  eActionStatus = EActionStatus;
  getNumberArray = ArrayUtils.getNumberArray;
  results!: ISearchResult[];
  first: number = 0;
  rows: number = 5;
  firstIndex = 0;
  totalPages = 0;
  firstRowIndex = 0;
  pageNumber = 1;
  pageSize = 5;
  pageSizeOptions = [5, 10, 15];
  totalRecords!: number;
  sortField = 'created';
  sortOption = ESortOption.DESC;
  eSortOption = ESortOption;
  private readonly unsubscribe$ = new Subject<void>();
  selectedOrganization!: number | null | undefined;
  selectedCountryId!: number | null | undefined;

  constructor() {
    this.searchState = toSignal(this.store.select(selectSearchState), {
      initialValue: {
        status: EActionStatus.Initial,
        successResponse: null,
        failureReason: '',
        errorMessage: '',
      },
    });
    effect(() => {
      const state = this.searchState();
      if (state.successResponse) {
        this.results = state.successResponse.results;
        this.totalRecords = state.successResponse.totalCount ?? 0;
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.first = 0;
      this.pageNumber = 1;
      const term = params['query'] ?? '';
      if (!term) return;

      this.store
        .select(selectCustomerOrganization)
        .pipe(
          takeUntil(this.unsubscribe$),
          tap((state: ICustomerOrganization | null) => {
            if (state?.id) {
              this.selectedOrganization = state?.organizationId;
              this.selectedCountryId = state?.organization?.countryId;

              this.store.dispatch(
                searchAction({
                  request: {
                    term,
                    pageNumber: this.pageNumber,
                    pageSize: this.pageSize,
                    countryId: this.selectedCountryId ?? 0,
                    organizationId: this.selectedOrganization ?? 0,
                  },
                })
              );
            }
          })
        )
        .subscribe();
    });
  }

  loadResults(event: TableLazyLoadEvent, isSearch = false) {
    this.firstRowIndex = isSearch ? 0 : this.first;
    this.pageSize = this.rows;
    this.pageNumber = this.firstRowIndex / this.pageSize + 1;
    this.sortField =
      ((Array.isArray(event.sortField) && event.sortField.length > 0
        ? event.sortField[0]
        : event.sortField) as string) ?? this.sortField;
    this.sortOption = event.sortOrder ?? this.sortOption;
  }

  onPageChange(event: PaginatorState) {
    const first = event.first ?? 0;
    const rows = event.rows ?? 5;

    this.first = first;
    this.rows = rows;

    const pageNumber = first / rows + 1;
    const term = this.router.routerState.snapshot.root.queryParams['query'] ?? '';

    this.store.dispatch(
      searchAction({
        request: {
          term,
          pageNumber,
          pageSize: rows,
          countryId: this.selectedCountryId ?? 0,
          organizationId: this.selectedOrganization ?? 0,
        },
      })
    );
  }

  viewObject(result: ISearchResult) {
    const url =
      result.objectType === 'option'
        ? `/product?id=${result.productId}&variantId=${result.objectID}`
        : `/product?id=${result.objectID}`;
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
